import type { Observation, Quantity } from '@medplum/fhirtypes';

import type { ChoiceValue } from '../../resolvers/choice';

/** Observation and ObservationComponent both carry these — helpers accept either. */
type Interpretable = Pick<Observation, 'interpretation' | 'referenceRange'>;

/** Minimal display formatting for choice values. Grows with the curated set. */
export function formatChoiceValue(choice: ChoiceValue): string {
  switch (choice.type) {
    case 'quantity': {
      const { value, unit, code } = choice.value;
      return [value, unit ?? code]
        .filter((part) => part !== undefined)
        .join(' ');
    }
    case 'codeableConcept':
      return choice.value.text ?? choice.value.coding?.[0]?.display ?? '';
    case 'string':
    case 'dateTime':
    case 'date':
      return choice.value;
    case 'number':
      return String(choice.value);
    case 'boolean':
      return choice.value ? 'Yes' : 'No';
    default:
      return '';
  }
}

export type InterpretationTone = 'negative' | 'warning';

export type InterpretationFlag = {
  label: string;
  tone: InterpretationTone;
};

/**
 * v3-ObservationInterpretation codes worth flagging. Normal ('N') and
 * unmapped codes render no flag — absence of a flag reads as unremarkable.
 */
const INTERPRETATION_FLAGS: Record<string, InterpretationFlag> = {
  HH: { label: 'Critical high', tone: 'negative' },
  LL: { label: 'Critical low', tone: 'negative' },
  AA: { label: 'Critical', tone: 'negative' },
  H: { label: 'High', tone: 'warning' },
  L: { label: 'Low', tone: 'warning' },
  A: { label: 'Abnormal', tone: 'warning' },
};

export function interpretationFlag(
  data: Interpretable,
): InterpretationFlag | undefined {
  const code = data.interpretation?.[0]?.coding?.[0]?.code;
  return code ? INTERPRETATION_FLAGS[code] : undefined;
}

/**
 * Most severe flag across a panel's components — lets a composed value
 * ("158/96 mmHg") carry its components' abnormality when the panel root has
 * no interpretation of its own. Severity: negative (critical) > warning.
 */
export function worstComponentFlag(
  data: Observation,
): InterpretationFlag | undefined {
  let worst: InterpretationFlag | undefined;
  for (const component of data.component ?? []) {
    const flag = interpretationFlag(component);
    if (!flag) continue;
    if (!worst || (flag.tone === 'negative' && worst.tone !== 'negative')) {
      worst = flag;
    }
  }
  return worst;
}

function quantityText(quantity: Quantity | undefined): string | undefined {
  if (quantity?.value === undefined) return undefined;
  return String(quantity.value);
}

/** "3.5–5.5 mmol/L" from the first reference range; explicit text wins. */
export function referenceRangeText(data: Interpretable): string | undefined {
  const range = data.referenceRange?.[0];
  if (!range) return undefined;
  if (range.text) return range.text;
  const low = quantityText(range.low);
  const high = quantityText(range.high);
  if (low === undefined && high === undefined) return undefined;
  const unit = range.low?.unit ?? range.high?.unit ?? '';
  const bounds =
    low !== undefined && high !== undefined
      ? `${low}–${high}`
      : low !== undefined
        ? `≥${low}`
        : `≤${high}`;
  return unit ? `${bounds} ${unit}` : bounds;
}

/**
 * Deterministic short date for display ("Jun 12, 2026"); time dropped.
 * Rendered in UTC: date-only FHIR values parse as UTC midnight, and a local
 * formatter would show the previous day west of Greenwich.
 */
export function formatFhirDate(value: string | undefined): string | undefined {
  if (!value) return undefined;
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return value;
  return parsed.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    timeZone: 'UTC',
  });
}

/** The observation's clinically relevant time: effective first, issued as fallback. */
export function observationDateText(data: Observation): string | undefined {
  return formatFhirDate(
    data.effectiveDateTime ?? data.effectivePeriod?.start ?? data.issued,
  );
}
